import { useEffect } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";
import { supabase } from "@/integrations/supabase/client";
import { connectedFileReadSessionId } from "@/lib/connectedFileRead";
import { assertConnectedFileImportGuard, subscribeConnectedFile, takeConnectedFile, type ConnectedFileImportGuard } from "@/lib/connectedFiles";

/** Mount in the composer; uses the existing upload/register/ingest chips and quotas. */
export function useConnectedFileImport(attach: (files: File[], expectedOwnerId?: string, guard?: ConnectedFileImportGuard) => Promise<void>) {
  const identity = useLibraryIdentity();
  const { t } = useLanguage();
  useEffect(() => {
    if (!identity.ready || identity.error || !identity.userId) return;
    const ownerId = identity.userId;
    let timer: ReturnType<typeof setTimeout>;
    let alive = true;
    const consuming = new Set<AbortController>();
    const receive = () => {
      clearTimeout(timer);
      // StrictMode's initial cleanup happens before the selection is consumed.
      timer = setTimeout(() => {
        if (!alive) return;
        const item = takeConnectedFile(ownerId);
        if (!item) return;
        // Only historical unbound entries use the owner-only handoff.
        if (!item.origin) { void attach([item.file], ownerId).catch(() => { if (alive) toast.error(t("mirror.files.full.error")); }); return; }
        const controller = new AbortController(); consuming.add(controller);
        const guard: ConnectedFileImportGuard = { origin: item.origin, signal: controller.signal, onStarted: () => { consuming.delete(controller); clearTimeout(timeout); subscription.unsubscribe(); } };
        const subscription = supabase.auth.onAuthStateChange((_event, session) => {
          if (session?.user.id !== ownerId || connectedFileReadSessionId(session.access_token) !== guard.origin.authSessionId) controller.abort();
        }).data.subscription;
        const timeout = setTimeout(() => controller.abort(), Math.max(0, Math.min(35_000, item.expiresAt - Date.now())));
        void (async () => {
          await assertConnectedFileImportGuard(guard);
          if (!alive || controller.signal.aborted || item.expiresAt <= Date.now()) return;
          await attach([item.file], ownerId, guard);
          // attach independently validates the origin after its own awaited getUser.
        })().catch(() => { if (alive && !controller.signal.aborted) toast.error(t("mirror.files.full.accountChanged")); }).finally(() => {
          consuming.delete(controller); clearTimeout(timeout); subscription.unsubscribe();
        });
      }, 0);
    };
    const unsubscribe = subscribeConnectedFile(receive);
    receive();
    // Once attach has started its pipelines, their own source guards take over.
    return () => { alive = false; clearTimeout(timer); consuming.forEach(controller => controller.abort()); unsubscribe(); };
  }, [identity.ready, identity.error, identity.userId, attach, t]);
}
